import React from 'react';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import { Button, Space, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ColumnType } from 'antd/es/table';
import CommonTable from 'components/CommonTable';
import {
    EFreeCreditBillingStatus,
    EFreeCreditOrderKey,
    TBillingFreeCreditItem,
    TGetPaymentBillingFreeCreditList,
} from 'types/financials';
import usePaymentBillingFreeCreditList from 'hooks/usePaymentBillingFreeCreditList';
import ProviderCell from 'components/ProviderCell';

interface Props {
    clientId?: number;
    isPractice?: boolean;
}
const BillingFreeCreditsTable = ({ clientId, isPractice = false }: Props) => {
    const [searchSourceText, setSearchSourceText] = React.useState('');
    const [searchPendingSourceText, setSearchPendingSourceText] = React.useState('');

    const [page, setPage] = React.useState(1);
    const [orderType, setOrderType] = React.useState<'ASC' | 'DESC'>('DESC');
    const [orderKey, setOrderKey] = React.useState<EFreeCreditOrderKey | undefined>(undefined);

    const [dataList, listTotal, loading, refetch] = usePaymentBillingFreeCreditList(undefined, isPractice);

    // 处理表格变化
    const handleTableChange = (paginationData: any, filtersData?: any, sorter?: any, action?: any) => {
        if (action?.action === 'paginate') {
            setPage(paginationData.current!);
            return;
        }

        if (sorter) {
            setOrderType(sorter.order === 'ascend' ? 'ASC' : 'DESC');
            if (sorter.column?.dataIndex === 'totalCentsAmount') {
                setOrderKey(EFreeCreditOrderKey.AMOUNT);
            }
            if (sorter.column?.dataIndex === 'createdAt') {
                setOrderKey(EFreeCreditOrderKey.ISSUED_DATE);
            }
        }
        if (filtersData) {
            setBillingStatus(filtersData.billingStatus || []);
        }
    };

    const refetchData = () => {
        const filter: Partial<TGetPaymentBillingFreeCreditList> = {};
        filter.current = page;
        filter.size = 10;
        if (orderKey) {
            filter.providerCreditsOrderKey = orderKey;
        }
        if (orderType) {
            filter.orderType = orderType;
        }
        refetch(filter);
    };

    React.useEffect(() => {
        refetchData();
    }, [page, orderType, orderKey, searchSourceText]);

    const allColumns: ColumnType<TBillingFreeCreditItem>[] = [
        {
            title: 'AMOUNT',
            dataIndex: 'totalCentsAmount',
            key: 'totalCentsAmount',
            width: 240,
            sorter: (a: TBillingFreeCreditItem, b: TBillingFreeCreditItem) => a.totalCentsAmount - b.totalCentsAmount,
            render: (fee: number) => {
                return <div className={s.tableItem}>${fee}</div>;
            },
        },
        {
            title: 'SOURCE',
            dataIndex: 'reason',
            key: 'reason',
            width: 120,
            filterDropdown: (props: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search by source"
                        value={searchPendingSourceText}
                        onChange={(e) => setSearchPendingSourceText(e.target.value)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setSearchSourceText(searchPendingSourceText);
                                props.confirm();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setSearchPendingSourceText('');
                                setSearchSourceText('');
                                props.clearFilters();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined />,
            onFilter: () => true, // 我们使用自己的过滤逻辑
        },
        ...(isPractice
            ? [
                  {
                      title: 'PROVIDER',
                      key: 'providerName',
                      width: 150,
                      render: (_: string, record: TBillingFreeCreditItem) => (
                          <ProviderCell
                              name={record.providerName}
                              avatar={record.providerAvatar}
                              credential={record.providerCredential}
                          />
                      ),
                  } as ColumnType<TBillingFreeCreditItem>,
              ]
            : []),
        {
            title: 'ISSUED DATE',
            dataIndex: 'issuedDate',
            key: 'issuedDate',
            width: 240,
            render: (issuedDate: string, item: TBillingFreeCreditItem) => {
                if (!item.createdAt || !item.createdAt) {
                    return '-';
                }
                // 03/26/2023, 10:30 - 11:00 AM
                return <div className={s.date}>{dayjs(issuedDate).format('MM/DD/YYYY')}</div>;
            },
            sorter: (a: TBillingFreeCreditItem, b: TBillingFreeCreditItem) => {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            },
        },
    ];

    const columns = clientId ? allColumns.filter((col) => col.key !== 'providerFirstName') : allColumns;

    return (
        <div className={s.wrap}>
            <CommonTable
                className={`commonTable ${s.paymentTable}`}
                columns={columns}
                data={dataList || []}
                loading={loading}
                bordered
                rowKey="appointmentId"
                onChange={handleTableChange}
                pagination={{
                    pageSize: 10,
                    current: page,
                    showSizeChanger: false,
                    total: listTotal,
                    showTotal: (total, range) => `${total} records`,
                }}
                scroll={{ x: 'max-content' }}
                locale={{
                    emptyText: 'No earnings yet',
                }}
            />
        </div>
    );
};

export default BillingFreeCreditsTable;
